import { DomainHit, ProviderId, ProviderResponse } from './types';
import { normalizeUrlToDomain, getFavicon, deduplicateUrls } from './normalize';

export const aggregateResultsMultiProvider = (
  results: Array<{
    query: string;
    provider: ProviderId;
    response: ProviderResponse;
  }>,
  queries: string[]
): Map<string, DomainHit> => {
  const domainMap = new Map<string, DomainHit>();

  results.forEach(({ query, provider, response }) => {
    // Process used URLs - prevent double-counting same domain in one query
    const usedDomains = new Set<string>();
    response.used_urls?.forEach((url) => {
      const domain = normalizeUrlToDomain(url);
      if (domain && !usedDomains.has(domain)) {
        usedDomains.add(domain);
        
        if (!domainMap.has(domain)) {
          domainMap.set(domain, {
            domain,
            usedBy: new Set(),
            consideredBy: new Set(),
            usedUrls: [],
            consideredUrls: [],
            providers: new Set(),
            providerBreakdown: {},
            queryBreakdown: {},
            favicon: getFavicon(domain),
          });
        }

        const hit = domainMap.get(domain)!;
        hit.usedBy.add(query);
        hit.providers.add(provider);
        
        if (!hit.providerBreakdown[provider]) {
          hit.providerBreakdown[provider] = { 
            usedCount: 0, 
            consideredCount: 0,
            usedUrls: [],
            consideredUrls: []
          };
        }
        hit.providerBreakdown[provider].usedCount++;
        hit.providerBreakdown[provider].usedUrls.push(url);
        
        // Track per-query URLs
        if (!hit.queryBreakdown![query]) {
          hit.queryBreakdown![query] = {
            usedUrls: [],
            consideredUrls: [],
            providers: new Set(),
            providerByUrl: {},
          };
        }
        hit.queryBreakdown![query].usedUrls.push(url);
        hit.queryBreakdown![query].providers.add(provider);
        if (!hit.queryBreakdown![query].providerByUrl[url]) {
          hit.queryBreakdown![query].providerByUrl[url] = new Set();
        }
        hit.queryBreakdown![query].providerByUrl[url].add(provider);
        
        if (hit.usedUrls.length < 5) {
          hit.usedUrls.push(url);
        }
      }
    });

    // Process considered URLs - prevent double-counting same domain in one query
    const consideredDomains = new Set<string>();
    response.considered?.forEach(({ url }) => {
      const domain = normalizeUrlToDomain(url);
      if (domain && !consideredDomains.has(domain)) {
        consideredDomains.add(domain);
        
        if (!domainMap.has(domain)) {
          domainMap.set(domain, {
            domain,
            usedBy: new Set(),
            consideredBy: new Set(),
            usedUrls: [],
            consideredUrls: [],
            providers: new Set(),
            providerBreakdown: {},
            queryBreakdown: {},
            favicon: getFavicon(domain),
          });
        }

        const hit = domainMap.get(domain)!;
        hit.consideredBy.add(query);
        hit.providers.add(provider);
        
        if (!hit.providerBreakdown[provider]) {
          hit.providerBreakdown[provider] = { 
            usedCount: 0, 
            consideredCount: 0,
            usedUrls: [],
            consideredUrls: []
          };
        }
        hit.providerBreakdown[provider].consideredCount++;
        hit.providerBreakdown[provider].consideredUrls.push(url);
        
        // Track per-query URLs
        if (!hit.queryBreakdown![query]) {
          hit.queryBreakdown![query] = {
            usedUrls: [],
            consideredUrls: [],
            providers: new Set(),
            providerByUrl: {},
          };
        }
        hit.queryBreakdown![query].consideredUrls.push(url);
        hit.queryBreakdown![query].providers.add(provider);
        if (!hit.queryBreakdown![query].providerByUrl[url]) {
          hit.queryBreakdown![query].providerByUrl[url] = new Set();
        }
        hit.queryBreakdown![query].providerByUrl[url].add(provider);
        
        if (hit.consideredUrls.length < 5) {
          hit.consideredUrls.push(url);
        }
      }
    });
  });

  // Final deduplication of sample URLs
  for (const hit of domainMap.values()) {
    hit.usedUrls = deduplicateUrls(hit.usedUrls);
    hit.consideredUrls = deduplicateUrls(hit.consideredUrls);
    for (const providerId in hit.providerBreakdown) {
      const breakdown = hit.providerBreakdown[providerId as ProviderId];
      if (breakdown) {
        breakdown.usedUrls = deduplicateUrls(breakdown.usedUrls);
        breakdown.consideredUrls = deduplicateUrls(breakdown.consideredUrls);
      }
    }
    // Deduplicate query-specific URLs
    if (hit.queryBreakdown) {
      for (const query in hit.queryBreakdown) {
        const queryData = hit.queryBreakdown[query];
        queryData.usedUrls = deduplicateUrls(queryData.usedUrls);
        queryData.consideredUrls = deduplicateUrls(queryData.consideredUrls);
      }
    }
  }

  return domainMap;
};

/**
 * Legacy exports - use selectors.ts instead
 * @deprecated
 */
export { calculateWeightedScore, calculateCoveragePercent } from './selectors';
